import { ApolloClient, useApolloClient } from "@apollo/client";
import { getDataOrThrow } from "@msys/common";
import React from "react";
import { PimSearchProductsFilters } from "../../../clients/graphqlTypes.js";
import {
  BrandPreferenceBrandLinesDocument,
  BrandPreferenceBrandLinesQuery,
  BrandPreferenceBrandLinesQueryVariables,
  BrandPreferenceLineFragment,
} from "./BrandPreferences.generated.js";
import {
  BrandPreferenceOption_BrandFragment,
  BrandPreferenceOptionsAvailableFiltersDocument,
  BrandPreferenceOptionsAvailableFiltersQuery,
  BrandPreferenceOptionsAvailableFiltersQueryVariables,
  BrandPreferenceOptionsBrandsDocument,
  BrandPreferenceOptionsBrandsQuery,
  BrandPreferenceOptionsBrandsQueryVariables,
} from "./brandPreferenceOptions.generated.js";

/** Mirrors the backend's cap on the lines of one brand preference. */
export const BRAND_PREFERENCE_LINE_LIMIT = 100;

/** A brand the preferences screen can pick. */
export interface BrandPreferenceOption {
  id: string;
  name: string;
}

/** Where the offered brands must be available: in one product type, or anywhere. */
export type BrandAvailabilityScope =
  | { productTypeId: string }
  | { global: true };

/** As {@link BrandAvailabilityScope}, or nowhere for a product type the catalogue does not know. */
export type BrandLineAvailabilityScope =
  | BrandAvailabilityScope
  | { nowhere: true };

export interface AvailableOptions<T> {
  loading: boolean;
  failed: boolean;
  options: T[];
}

export type AvailableBrandPreferenceOptions =
  AvailableOptions<BrandPreferenceOption>;

export type UseAvailableBrandPreferenceOptions = (
  availableIn: BrandAvailabilityScope
) => AvailableBrandPreferenceOptions;

type FacetBucket = { key: string };

const PAGE_SIZE = 500;

export function useAvailableBrandOptions(
  availableIn: BrandAvailabilityScope
): AvailableBrandPreferenceOptions {
  return useAvailableOptions(availableIn, loadAvailableBrandOptions);
}

export function useAvailableBrandLineOptions(
  brand: { id: string; title: string },
  availableIn: BrandLineAvailabilityScope
): AvailableOptions<BrandPreferenceLineFragment> {
  const { id, title } = brand;
  const load = React.useCallback<LoadOptions<BrandPreferenceLineFragment>>(
    (client, filters) =>
      loadAvailableBrandLineOptions(client, { id, title }, filters),
    [id, title]
  );
  return useAvailableOptions(availableIn, load);
}

type LoadOptions<T> = (
  client: ApolloClient<object>,
  filters: PimSearchProductsFilters | null
) => Promise<T[]>;

/** Loads once per scope, not per search term: the picker filters the result itself. */
function useAvailableOptions<T>(
  availableIn: BrandLineAvailabilityScope,
  load: LoadOptions<T>
): AvailableOptions<T> {
  const client = useApolloClient();
  const nowhere = "nowhere" in availableIn;
  const productTypeId =
    "productTypeId" in availableIn ? availableIn.productTypeId : null;
  const [state, setState] = React.useState<AvailableOptions<T>>({
    loading: !nowhere,
    failed: false,
    options: [],
  });

  React.useEffect(() => {
    if (nowhere) {
      setState({ loading: false, failed: false, options: [] });
      return;
    }
    let cancelled = false;
    setState({ loading: true, failed: false, options: [] });
    load(
      client,
      productTypeId === null ? null : { productTypeIds: [productTypeId] }
    ).then(
      options => {
        if (!cancelled) setState({ loading: false, failed: false, options });
      },
      () => {
        if (!cancelled) setState({ loading: false, failed: true, options: [] });
      }
    );
    return () => {
      cancelled = true;
    };
  }, [client, nowhere, productTypeId, load]);

  return state;
}

const loadAvailableBrandOptions: LoadOptions<BrandPreferenceOption> = async (
  client,
  filters
) => {
  const facet = await fetchAvailableFilters(client, filters);
  return availableBrandOptions(await fetchBrands(client), facet.brands);
};

async function loadAvailableBrandLineOptions(
  client: ApolloClient<object>,
  brand: { id: string; title: string },
  filters: PimSearchProductsFilters | null
): Promise<BrandPreferenceLineFragment[]> {
  const [lines, facet] = await Promise.all([
    fetchBrandLines(client, brand.id),
    fetchAvailableFilters(client, { ...filters, brands: [brand.title] }),
  ]);
  return availableBrandLineOptions(lines, facet.brandLines);
}

async function fetchAvailableFilters(
  client: ApolloClient<object>,
  filters: PimSearchProductsFilters | null
) {
  const { data } = await client.query<
    BrandPreferenceOptionsAvailableFiltersQuery,
    BrandPreferenceOptionsAvailableFiltersQueryVariables
  >({
    query: BrandPreferenceOptionsAvailableFiltersDocument,
    variables: { filters },
  });
  return getDataOrThrow(data.pimSearchProductsAvailableFilters);
}

function fetchBrands(client: ApolloClient<object>) {
  return fetchAllPages(async offset => {
    const { data } = await client.query<
      BrandPreferenceOptionsBrandsQuery,
      BrandPreferenceOptionsBrandsQueryVariables
    >({
      query: BrandPreferenceOptionsBrandsDocument,
      variables: { limit: PAGE_SIZE, offset },
    });
    const result = getDataOrThrow(data.pimSearchBrands);
    return { items: result.brands, totalCount: result.totalCount };
  });
}

function fetchBrandLines(client: ApolloClient<object>, brandId: string) {
  return fetchAllPages(async offset => {
    const { data } = await client.query<
      BrandPreferenceBrandLinesQuery,
      BrandPreferenceBrandLinesQueryVariables
    >({
      query: BrandPreferenceBrandLinesDocument,
      variables: { brandId, limit: PAGE_SIZE, offset },
    });
    const result = getDataOrThrow(data.pimSearchBrandLines);
    return { items: result.brandLines, totalCount: result.totalCount };
  });
}

export async function fetchAllPages<T>(
  fetchPage: (offset: number) => Promise<{ items: T[]; totalCount: number }>,
  offset = 0
): Promise<T[]> {
  const page = await fetchPage(offset);
  const next = offset + page.items.length;
  return page.items.length === 0 || next >= page.totalCount
    ? page.items
    : [...page.items, ...(await fetchAllPages(fetchPage, next))];
}

/** The facet buckets carry brand titles, which are unique, so the join is exact. */
export function availableBrandOptions(
  brands: Pick<BrandPreferenceOption_BrandFragment, "id" | "title">[],
  brandFacet: FacetBucket[]
): BrandPreferenceOption[] {
  const titles = new Set(brandFacet.map(bucket => bucket.key));
  return sortByName(
    brands
      .filter(brand => titles.has(brand.title))
      .map(brand => ({
        id: brand.id,
        name: brand.title,
      }))
  );
}

type Line = {
  id: string;
  title: string;
  mainBrandLine?: { id: string; title: string } | null;
};

/**
 * Joins one brand's lines with the line facet of that brand's products. A main line is kept
 * through its kept sub-lines, as the facet counts only a product's direct line.
 */
export function availableBrandLineOptions<T extends Line>(
  lines: readonly T[],
  brandLineFacet: FacetBucket[]
): T[] {
  const titles = new Set(brandLineFacet.map(bucket => bucket.key));
  const kept = lines.filter(line => titles.has(line.title));
  const mainLineIdsOfKept = new Set(
    kept.flatMap(line => (line.mainBrandLine ? [line.mainBrandLine.id] : []))
  );
  return lines
    .filter(line => titles.has(line.title) || mainLineIdsOfKept.has(line.id))
    .sort((a, b) => lineName(a).localeCompare(lineName(b)));
}

/** A line within its brand: a sub-line is named with its main line. */
export function lineName(line: {
  title: string;
  mainBrandLine?: { title: string } | null;
}): string {
  return line.mainBrandLine
    ? `${line.mainBrandLine.title} › ${line.title}`
    : line.title;
}

export function filterOptionsByName(
  options: BrandPreferenceOption[],
  searchTerm: string
): BrandPreferenceOption[] {
  const term = searchTerm.trim().toLowerCase();
  return term === ""
    ? options
    : options.filter(option => option.name.toLowerCase().includes(term));
}

function sortByName(options: BrandPreferenceOption[]): BrandPreferenceOption[] {
  return [...options].sort((a, b) => a.name.localeCompare(b.name));
}
